import { memo, useState } from "react";
import { classNames, useTranslate } from "helpers";
import { ReactSVG } from "react-svg";
import { ContractBuyerCardProps } from "./types";
import ContactEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactEditCard/ContactEditCard";
import { getContactAPI, getContactRelationsAPI, getContactPhonesAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";

const BUYER_FIELDS = [
  "buyerName",
  "buyerPersonalIdentificationNumber",
  "buyerAddress",
  "buyerPhoneNumber",
  "buyerMobilePhoneNumber",
  "buyerEmail",
  "buyerAuthorizedRepresentativeName",
] as const;

const InfoRow: React.FC<{ label: string; value: string | null; hasError?: boolean }> = ({ label, value, hasError }) => (
  <div className="flex justify-between py-1.5">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={classNames(
      "text-sm font-medium",
      value ? "text-green-700" : hasError ? "text-red-500" : "text-slate-900"
    )}>{value || (hasError ? "Potrebno popuniti" : "—")}</span>
  </div>
);

const ContractBuyerCard: React.FC<ContractBuyerCardProps> = ({
  formValues,
  setFormValues,
  errors,
  setErrors,
}) => {

  const { t } = useTranslate();
  const [showEditModal, setShowEditModal] = useState(false);

  const phoneOrMobileFilled = !!formValues.buyerPhoneNumber || !!formValues.buyerMobilePhoneNumber;

  const hasErrors = BUYER_FIELDS.some(field => {
    if ((field === "buyerPhoneNumber" || field === "buyerMobilePhoneNumber") && phoneOrMobileFilled) return false;
    return !!errors[field];
  });

  const handleOpenModal = () => {
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
  };

  const handleEditSuccess = async () => {
    setShowEditModal(false);

    if (!formValues.buyerContactId) return;

    try {
      const contact = await getContactAPI(formValues.buyerContactId);
      const isCompany = contact.contactType?.id === "C";

      const phone = contact.primaryContactPhone;
      const isMobile = phone?.phoneTypeId === "M";

      const address = contact.primaryContactAddress;
      const formattedAddress = address
        ? [
            address.line1,
            address.line2,
            [address.postalCode, address.city].filter(Boolean).join(" "),
            address.country,
          ].filter(Boolean).join(", ")
        : null;

      let representativeName: string | null = null;
      let representativeEmail: string | null = null;
      let representativeFixedPhone: string | null = null;
      let representativeMobilePhone: string | null = null;

      if (isCompany) {
        const relations = await getContactRelationsAPI(formValues.buyerContactId);
        if (relations.length > 0) {
          const rep = relations[0];
          representativeName = `${rep.personContact?.firstName || ""} ${rep.personContact?.lastName || ""}`.trim() || null;
          representativeEmail = rep.personContact?.primaryContactEmail?.emailAddress || null;

          if (rep.personContact?.id) {
            const phones = await getContactPhonesAPI(initialDataWithPagination.pagination, rep.personContact.id);
            representativeFixedPhone = phones.data.find(p => p.phoneTypeId === "F")?.phoneNumber || null;
            representativeMobilePhone = phones.data.find(p => p.phoneTypeId === "M")?.phoneNumber || null;
          }
        }
      }

      setFormValues(prev => ({
        ...prev,
        buyerName: contact.name || contact.companyName || `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || null,
        buyerPersonalIdentificationNumber: contact.personalIdentificationNumber || null,
        buyerAddress: formattedAddress,
        buyerPhoneNumber: isCompany
          ? (representativeFixedPhone || (!isMobile ? phone?.phoneNumber : null) || null)
          : (!isMobile ? phone?.phoneNumber || null : null),
        buyerMobilePhoneNumber: isCompany
          ? (representativeMobilePhone || (isMobile ? phone?.phoneNumber : null) || null)
          : (isMobile ? phone?.phoneNumber || null : null),
        buyerEmail: isCompany
          ? (representativeEmail || contact.primaryContactEmail?.emailAddress || null)
          : (contact.primaryContactEmail?.emailAddress || null),
        buyerAuthorizedRepresentativeName: isCompany
          ? representativeName
          : `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || null,
        buyerContactTypeId: contact.contactType?.id ?? null,
      }));

      setErrors(prev => {
        const next = { ...prev };
        BUYER_FIELDS.forEach(field => delete next[field]);
        return next;
      });
    } catch {
      // error handled by snackbar
    }
  };

  return (
    <>
      <div className={classNames(
        "flex-1 bg-white rounded-sm border overflow",
        hasErrors ? "border-red-300" : "border-slate-300"
      )}>
        <div className={classNames(
          "flex justify-between items-center px-3.5 py-2.5",
          hasErrors ? "bg-red-50" : "bg-slate-50"
        )}>
          <div className="flex items-center gap-2">
            <span className="text-md font-medium text-slate-900">Kupac</span>
            {hasErrors && (
              <span className="w-2 h-2 rounded-full bg-red-500" />
            )}
          </div>
          <button type="button" onClick={handleOpenModal} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-sky-500 bg-sky-50 text-sky-700 text-sm font-medium hover:bg-sky-100 transition-colors">
            <ReactSVG src="/media/icons/edit.svg" className="[&_svg]:w-3.5 [&_svg]:h-3.5 [&_path]:stroke-sky-600" />
            Uredi
          </button>
        </div>
        <div className={classNames("flex flex-col px-3.5 py-3", hasErrors && "bg-white")}>
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyername")} value={formValues.buyerName} hasError={!!errors.buyerName} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyerpersonalidentificationnumber")} value={formValues.buyerPersonalIdentificationNumber} hasError={!!errors.buyerPersonalIdentificationNumber} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyeraddress")} value={formValues.buyerAddress} hasError={!!errors.buyerAddress} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyerphonenumber")} value={formValues.buyerPhoneNumber} hasError={!formValues.buyerPhoneNumber && !formValues.buyerMobilePhoneNumber && !!errors.buyerMobilePhoneNumber} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyermobilephonenumber")} value={formValues.buyerMobilePhoneNumber} hasError={!formValues.buyerPhoneNumber && !formValues.buyerMobilePhoneNumber && !!errors.buyerMobilePhoneNumber} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyeremail")} value={formValues.buyerEmail} hasError={!!errors.buyerEmail} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyerauthorizedrepresentativename")} value={formValues.buyerAuthorizedRepresentativeName} hasError={!!errors.buyerAuthorizedRepresentativeName} />
        </div>
      </div>

      {formValues.buyerContactId && (
        <ContactEditCard
          contactId={formValues.buyerContactId}
          show={showEditModal}
          onSuccess={handleEditSuccess}
          onClose={handleCloseModal}
          isFullScreen={false}
        />
      )}
    </>
  );
};

export default memo(ContractBuyerCard);
